import React, { useState, useEffect } from "react";
import {
  getMyPromoCode,
  generateSpecialPromoCode,
  getSpecialPromoCode,
} from "../services/promo-code-service";
import { FaCopy, FaCheck, FaCoins, FaGift } from "react-icons/fa";

interface MyPromoCodeCardProps {
  userId: string;
  userCoins?: number;
  maxCoinsCap?: number;
}

const MyPromoCodeCard: React.FC<MyPromoCodeCardProps> = ({
  userId,
  userCoins = 0,
  maxCoinsCap = 50,
}) => {
  const [promoCode, setPromoCode] = useState<string>("");
  const [discountPercentage, setDiscountPercentage] = useState<number>(0);
  const [usageCount, setUsageCount] = useState<number>(0);
  const [ownerCoins, setOwnerCoins] = useState<number>(userCoins);
  const [specialPromoCode, setSpecialPromoCode] = useState<string | null>(null);
  const [specialLoading, setSpecialLoading] = useState(false);
  const [ownerRole, setOwnerRole] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchPromoCode = async () => {
      try {
        const data = await getMyPromoCode(userId);
        setPromoCode(data.code);
        setDiscountPercentage(data.discountPercentage);
        setUsageCount(data.usageCount);
        setOwnerCoins(
          typeof data.ownerCoins === "number" ? data.ownerCoins : userCoins,
        );
        setOwnerRole(data.ownerRole || "");

        const special = await getSpecialPromoCode(userId);
        setSpecialPromoCode((special as any)?.code || null);
      } catch (error) {
        console.error("Failed to fetch promo code:", error);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchPromoCode();
    }
  }, [userId, userCoins]);

  const handleCopy = () => {
    navigator.clipboard.writeText(promoCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isStudent = ownerRole === "ROLE_STUDENT";
  const isTeacher = ownerRole === "ROLE_TEACHER" || ownerRole === "ROLE_SUPER_TEACHER";
  const canGenerateSpecial = ownerCoins >= maxCoinsCap;

  const handleGenerateSpecial = async () => {
    if (!isStudent || !userId || !canGenerateSpecial) return;
    setSpecialLoading(true);
    try {
      const special = await generateSpecialPromoCode(userId);
      const code = (special as any)?.code;
      if (code) {
        setSpecialPromoCode(code);
        setOwnerCoins((prev) => Math.max(0, prev - maxCoinsCap));
      }
    } catch (error) {
      console.error("Failed to generate special promo code", error);
    } finally {
      setSpecialLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 border rounded-lg shadow-md promo-code-card bg-gradient-to-br from-blue-50 to-purple-50">
        <p className="text-gray-600">Loading your promo code...</p>
      </div>
    );
  }

  return (
    <div className="p-6 border rounded-lg shadow-md promo-code-card bg-gradient-to-br from-blue-50 to-purple-50">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-gray-800">Votre code promo personnel</h3>
        {isStudent && (
          <div className="flex items-center gap-2 px-3 py-1 bg-yellow-100 rounded-full">
            <FaCoins className="text-yellow-600" />
            <span className="text-sm font-bold text-yellow-700">
              {ownerCoins}/{maxCoinsCap} coins
            </span>
          </div>
        )}
      </div>

      <div className="p-4 mb-4 bg-white rounded-lg shadow-sm">
        <p className="mb-2 text-sm text-right text-gray-600">
          Partagez ce code avec vos amis pour profiter de vos avantages.
        </p>
        <div className="flex items-center justify-between p-3 border-2 border-blue-300 border-dashed rounded-md bg-gray-50">
          <span className="text-2xl font-bold tracking-wider text-blue-600" dir="ltr">
            {promoCode}
          </span>
          <button
            onClick={handleCopy}
            className="p-2 ml-4 transition-colors bg-blue-600 rounded-md text-primary_bg hover:bg-blue-700"
            title="Copier le code"
          >
            {copied ? <FaCheck /> : <FaCopy />}
          </button>
        </div>
        {copied && (
          <p className="mt-2 text-xs text-center text-green-600">Code copié !</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="p-4 bg-white rounded-lg shadow-sm">
          <p className="mb-1 text-sm text-right text-gray-600">Réduction</p>
          <p className="text-2xl font-bold text-right text-green-600">{discountPercentage}%</p>
        </div>
        <div className="p-4 bg-white rounded-lg shadow-sm">
          <p className="mb-1 text-sm text-right text-gray-600">Utilisations</p>
          <p className="text-2xl font-bold text-right text-purple-600">{usageCount}</p>
        </div>
      </div>

      {isStudent && (
        <div className="p-4 border-2 border-yellow-200 rounded-lg bg-yellow-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FaCoins className="text-xl text-yellow-500" />
              <span className="text-sm text-gray-700">NJE7EKCOINS</span>
            </div>
            <span className="text-2xl font-bold text-yellow-600">
              {ownerCoins}/{maxCoinsCap}
            </span>
          </div>
          <div className="p-3 mt-3 bg-white border border-yellow-300 rounded-lg">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-yellow-800">Code promo spécial (hors annuel)</p>
                <p className="text-xs text-gray-600">
                  Déduit 50 coins pour un code -50% sur toutes les périodes sauf annuelle.
                </p>
              </div>
              <button
                type="button"
                onClick={handleGenerateSpecial}
                disabled={!canGenerateSpecial || specialLoading}
                className={`px-3 py-2 text-xs font-bold rounded-lg border ${
                  (!canGenerateSpecial || specialLoading)
                    ? "border-gray-300 text-gray-400 bg-gray-100"
                    : "border-yellow-400 text-yellow-800 bg-white hover:bg-yellow-100"
                }`}
              >
                {specialLoading ? "..." : "Générer"}
              </button>
            </div>
            {specialPromoCode && (
              <div className="mt-2 text-xs font-semibold text-green-700">
                Code spécial : <span className="font-mono">{specialPromoCode}</span>
              </div>
            )}
            {!canGenerateSpecial && (
              <div className="mt-2 text-xs text-gray-600">
                Il vous reste {Math.max(0, maxCoinsCap - ownerCoins)} coins pour débloquer le code spécial.
              </div>
            )}
          </div>
        </div>
      )}

      {isTeacher && (
        <div className="p-4 border-2 border-green-200 rounded-lg bg-green-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FaGift className="text-xl text-green-500" />
              <span className="text-sm text-gray-700">Récompenses</span>
            </div>
            <span className="text-lg font-bold text-green-600">
              {usageCount >= 1 && usageCount <= 20 && "1-20 : 5 points"}
              {usageCount > 20 && usageCount <= 40 && "21-40 : 7 points"}
              {usageCount > 40 && "41+ : 8 points"}
            </span>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-xs text-center border border-green-200 rounded">
              <thead>
                <tr className="bg-green-100">
                  <th className="px-2 py-1 border border-green-200">Utilisations</th>
                  <th className="px-2 py-1 border border-green-200">Points</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="px-2 py-1 border border-green-200">1 - 20</td>
                  <td className="px-2 py-1 border border-green-200">5</td>
                </tr>
                <tr>
                  <td className="px-2 py-1 border border-green-200">21 - 40</td>
                  <td className="px-2 py-1 border border-green-200">7</td>
                </tr>
                <tr>
                  <td className="px-2 py-1 border border-green-200">41+</td>
                  <td className="px-2 py-1 border border-green-200">8</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyPromoCodeCard;
